import useSWR, { useSWRConfig } from 'swr';
import { useMemo } from 'react';
import { Artist } from 'types';
import api from 'utils/api';

export function useLikedArtists() {
  const { data, error, mutate } = useSWR<Artist[]>(
    'liked-artists',
    async () => {
      const likedArtists: Artist[] = await api.collabClub.getLikedArtists();
      return likedArtists;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2 * 60 * 1000, // 2 minutes
    }
  );

  // Memoize the liked artists array to prevent unnecessary re-renders
  const likedArtists = useMemo(() => data || [], [data]);

  return {
    likedArtists,
    isLoading: !data && !error,
    error,
    refetch: mutate,
  };
}

export function useArtistLikes() {
  const { mutate, cache } = useSWRConfig();
  const { likedArtists, refetch: refetchLiked } = useLikedArtists();

  const likeArtist = async (artistId: number) => {
    try {
      // Get the artist data from the main artists cache to add to liked artists
      const allArtistsData = cache.get('collab-club-artists')?.data as Artist[] | undefined;
      const artistToLike = allArtistsData?.find((artist) => artist.id === artistId);

      if (!artistToLike) {
        console.warn('Artist not found in cache, skipping optimistic update');
        // Still make the API call and refetch
        await api.collabClub.likeArtist({ data: { artistId } });
        mutate('liked-artists');
        return { success: true };
      }

      // Optimistically add to liked artists
      const optimisticUpdate = async (currentData: Artist[] | undefined) => {
        if (!currentData) return [artistToLike];
        // Check if already liked to avoid duplicates
        if (currentData.some((artist) => artist.id === artistId)) {
          return currentData;
        }
        return [...currentData, artistToLike];
      };

      // Optimistically update the cache without revalidation
      mutate('liked-artists', optimisticUpdate, false);

      // Make the API call in the background
      await api.collabClub.likeArtist({ data: { artistId } });

      // No need to refetch - optimistic update should be sufficient
      return { success: true };
    } catch (error: any) {
      console.error('Failed to like artist:', error);
      // Revert optimistic update on error
      refetchLiked();
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to like artist',
      };
    }
  };

  const unlikeArtist = async (artistId: number) => {
    try {
      // Optimistically remove from liked artists
      const optimisticUpdate = async (currentData: Artist[] | undefined) => {
        if (!currentData) return currentData;
        return currentData.filter((artist) => artist.id !== artistId);
      };

      // Optimistically update the cache
      mutate('liked-artists', optimisticUpdate, false);

      // Make the API call
      await api.collabClub.unlikeArtist({ id: artistId });

      // Revalidate both liked artists and all artists
      await Promise.all([
        mutate('liked-artists'),
        // mutate('collab-club-artists'),
      ]);

      return { success: true };
    } catch (error: any) {
      console.error('Failed to unlike artist:', error);
      // Revert optimistic update on error
      refetchLiked();
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to unlike artist',
      };
    }
  };

  const toggleLike = async (artistId: number, currentlyLiked: boolean) => {
    if (currentlyLiked) {
      return unlikeArtist(artistId);
    } else {
      return likeArtist(artistId);
    }
  };

  // Create a Set for quick lookup of liked artist IDs
  const likedArtistIds = useMemo(() => {
    return new Set(likedArtists.map((artist) => artist.id.toString()));
  }, [likedArtists]);

  return {
    likedArtists,
    likedArtistIds,
    likeArtist,
    unlikeArtist,
    toggleLike,
    isLoading: !likedArtists,
  };
}
